import { getBestTextColorOnBackground, toReadableHex } from "../../../../tools/ColorTools";
import { appendStopLabelContent } from "./stopLabel.content.tools";
import {
    STOP_LABEL_ABOVE_DOT_CONFIG,
    STOP_LABEL_CONTENT_ROW_CLASS,
    STOP_LABEL_NAME_ONLY_CLASS,
    STOP_LABEL_SHELL_BORDER_RADIUS_CLASS,
    STOP_LABEL_SHELL_LAYOUT_CLASS,
    STOP_LABEL_SHELL_PADDING_CLASS,
    STOP_LABEL_SHELL_TEXT_CLASS,
    applyStopLabelShellLayout,
    resolveStopLabelArrivalTimePillBorderRadiusClass,
} from "./stopLabel.placement";
import type { DimmedStopLabelPresentation } from "./stopLabel.presentation";
import type { StopLabelData, StopLabelPlacementConfig, StopLabelVisualVariant } from "./stopLabel.types";

/** Inputs for {@link createStopLabelElement}. */
export interface StopLabelElementPresentation {
    "variant":             StopLabelVisualVariant;
    "legColorHex":         string;
    "isDarkUi":            boolean;
    "dimmedPresentation"?: DimmedStopLabelPresentation;

    /** Itinerary endpoint role when the chip is anchored at the stop itself (no dot). */
    "endpointRole"?: "from" | "to";

    /**
     * When true with {@link StopLabelVisualVariant.endpointChip}, places the chip above
     * a separate map dot instead of using itinerary endpoint anchoring.
     */
    "aboveDot"?: boolean;
}

/**
 * Creates the circular marker element for a transit-path stop dot.
 *
 * @param legColorHex Leg color used for marker border.
 * @returns Styled dot element for a MapLibre marker.
 */
export function createStopLabelDot( legColorHex: string ): HTMLDivElement {
    const dot = document.createElement( "div" );
    dot.className =
        "pointer-events-none h-4 w-4 box-border rounded-full border-[3px] shadow-md";
    dot.style.borderColor = legColorHex;

    return dot;
}

/**
 * Creates a unified stop label element and placement metadata.
 *
 * @param presentation Visual variant and theme inputs.
 * @param data Optional enriched stop data for time/delay display.
 * @param fallbackLabel Plain label when `data` is omitted.
 * @returns Label element with marker anchor/offset configuration.
 */
export function createStopLabelElement(
    presentation: StopLabelElementPresentation,
    data?: StopLabelData,
    fallbackLabel?: string,
): {
    "element": HTMLDivElement;
    "config":  StopLabelPlacementConfig;
} {
    const { variant, legColorHex, isDarkUi, dimmedPresentation, endpointRole, aboveDot } = presentation;

    if ( variant === "endpointChip" ) {
        const wrap = document.createElement( "div" );
        wrap.className =
            `border shadow-md pointer-events-none ${ STOP_LABEL_SHELL_PADDING_CLASS } ${ STOP_LABEL_SHELL_TEXT_CLASS } ${ STOP_LABEL_SHELL_BORDER_RADIUS_CLASS } ${ STOP_LABEL_SHELL_LAYOUT_CLASS }`;
        applyStopLabelShellLayout( wrap );

        // Filled route-color chip (realtime next stop and itinerary from/to endpoints).
        if ( aboveDot === true ) {
            wrap.style.backgroundColor = toReadableHex( legColorHex );
            wrap.style.borderColor = isDarkUi ? "rgba(255,255,255,0.5)" : "rgba(15,23,42,0.2)";
            wrap.style.color = getBestTextColorOnBackground( legColorHex );
        } else {
            wrap.style.backgroundColor = isDarkUi ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.96)";
            wrap.style.borderColor = legColorHex;
            wrap.style.color = isDarkUi ? "#f8fafc" : "#0f172a";
        }

        if ( data !== undefined ) {
            appendStopLabelContent( wrap, data, isDarkUi, resolveStopLabelArrivalTimePillBorderRadiusClass( variant ) );
        } else if ( fallbackLabel !== undefined ) {
            wrap.className = `${ wrap.className } ${ STOP_LABEL_CONTENT_ROW_CLASS }`.trim();
            const nameEl = document.createElement( "span" );
            nameEl.className = STOP_LABEL_NAME_ONLY_CLASS;
            nameEl.textContent = fallbackLabel;
            nameEl.title = fallbackLabel;
            wrap.appendChild( nameEl );
        }

        if ( aboveDot === true ) {
            return {
                "element": wrap,
                "config":  STOP_LABEL_ABOVE_DOT_CONFIG,
            };
        }

        const anchor: StopLabelPlacementConfig["anchor"] = endpointRole === "to" ? "top" : "bottom";
        const offset: [number, number] = endpointRole === "to" ? [ 0, -40 ] : [ 0, 40 ];

        return {
            "element": wrap,
            "config":  {
                anchor,
                offset,
                "className": "text-left",
            },
        };
    }

    if ( variant === "terminusChip" ) {
        const wrap = document.createElement( "div" );
        wrap.className =
            `pointer-events-none border-2 border-dashed shadow-sm ${ STOP_LABEL_SHELL_PADDING_CLASS } ${ STOP_LABEL_SHELL_TEXT_CLASS } font-bold ${ STOP_LABEL_SHELL_BORDER_RADIUS_CLASS } ${ STOP_LABEL_SHELL_LAYOUT_CLASS }`;
        applyStopLabelShellLayout( wrap );
        wrap.style.backgroundColor = isDarkUi ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.96)";
        wrap.style.borderColor = legColorHex;
        wrap.style.color = legColorHex;

        if ( data !== undefined ) {
            appendStopLabelContent( wrap, data, isDarkUi, resolveStopLabelArrivalTimePillBorderRadiusClass( variant ) );
        } else if ( fallbackLabel !== undefined ) {
            wrap.className = `${ wrap.className } ${ STOP_LABEL_CONTENT_ROW_CLASS }`.trim();
            const nameEl = document.createElement( "span" );
            nameEl.className = STOP_LABEL_NAME_ONLY_CLASS;
            nameEl.textContent = fallbackLabel;
            nameEl.title = fallbackLabel;
            wrap.appendChild( nameEl );
        }

        return {
            "element": wrap,
            "config":  STOP_LABEL_ABOVE_DOT_CONFIG,
        };
    }

    const textEl = document.createElement( "div" );
    textEl.className =
        `pointer-events-none border shadow-sm ${ STOP_LABEL_SHELL_PADDING_CLASS } ${ STOP_LABEL_SHELL_TEXT_CLASS } ${ STOP_LABEL_SHELL_BORDER_RADIUS_CLASS } ${ STOP_LABEL_SHELL_LAYOUT_CLASS }`;
    applyStopLabelShellLayout( textEl );
    const textColor = isDarkUi ? "#f8fafc" : "#0f172a";
    textEl.style.color = textColor;
    textEl.style.backgroundColor = isDarkUi ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.96)";
    textEl.style.border = `1px solid ${ legColorHex }`;

    if ( data !== undefined ) {
        appendStopLabelContent( textEl, data, isDarkUi, resolveStopLabelArrivalTimePillBorderRadiusClass( variant ) );
    } else if ( fallbackLabel !== undefined ) {
        textEl.className = `${ textEl.className } ${ STOP_LABEL_CONTENT_ROW_CLASS }`.trim();
        const nameEl = document.createElement( "span" );
        nameEl.className = STOP_LABEL_NAME_ONLY_CLASS;
        nameEl.textContent = fallbackLabel;
        nameEl.title = fallbackLabel;
        textEl.appendChild( nameEl );
    }

    if ( variant === "dimmedBubble" && dimmedPresentation !== undefined ) {
        textEl.style.color = isDarkUi
            ? dimmedPresentation.labelTextColorDark
            : dimmedPresentation.labelTextColorLight;
        textEl.style.backgroundColor = isDarkUi
            ? dimmedPresentation.labelBackgroundDark
            : dimmedPresentation.labelBackgroundLight;
        textEl.style.borderColor = isDarkUi
            ? dimmedPresentation.labelBorderColorDark
            : dimmedPresentation.labelBorderColorLight;
    }

    return {
        "element": textEl,
        "config":  STOP_LABEL_ABOVE_DOT_CONFIG,
    };
}

/**
 * Applies dimmed presentation styles to a stop dot element.
 *
 * @param dot Stop dot element.
 * @param isDarkUi Whether dark theme is active.
 * @param legColorHex Active route color for non-dimmed shadow fallback.
 * @param dimmedPresentation Dimmed marker presentation constants.
 */
export function applyDimmedStopDotPresentation(
    dot: HTMLDivElement,
    isDarkUi: boolean,
    dimmedPresentation: DimmedStopLabelPresentation,
): void {
    dot.style.transform = `scale(${ dimmedPresentation.dotScale })`;
    dot.style.borderColor = isDarkUi
        ? dimmedPresentation.dotBorderColorDark
        : dimmedPresentation.dotBorderColorLight;
    dot.style.boxShadow = isDarkUi
        ? dimmedPresentation.dotBoxShadowDark
        : dimmedPresentation.dotBoxShadowLight;
    dot.style.backgroundColor = isDarkUi ? "#1e293b" : "#ffffff";
}

/**
 * Applies default active stop dot shadow styling.
 *
 * @param dot Stop dot element.
 * @param isDarkUi Whether dark theme is active.
 */
export function applyActiveStopDotPresentation( dot: HTMLDivElement, isDarkUi: boolean ): void {
    dot.style.backgroundColor = isDarkUi ? "#1e293b" : "#ffffff";
    dot.style.boxShadow = isDarkUi
        ? "0 0 0 1px rgba(255,255,255,0.45), 0 3px 8px rgba(0,0,0,0.45)"
        : "0 2px 6px rgba(15,23,42,0.25)";
}
